import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { KPI } from '../../../../@core/api';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NbAccordionModule, NbDialogService, NbToastrService } from '@nebular/theme';
import { AddEnergyObjectiveDialogComponent } from '../add-energy-objective-dialog/add-energy-objective-dialog.component';
import { AddCarbonObjectiveDialogComponent } from '../add-carbon-objective-dialog/add-carbon-objective-dialog.component';
import { EditEnergyObjectiveDialogComponent } from '../edit-energy-objective-dialog/edit-energy-objective-dialog.component';
import { EditCarbonObjectiveDialogComponent } from '../edit-carbon-objective-dialog/edit-carbon-objective-dialog.component';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { EnergyObjectivesService, EnergyObjective, CarbonObjective } from '../../../../@core/utils/energy-objectives.service';
import { Subscription } from 'rxjs';
import { SaveToastService } from '../../../../@core/utils';

@Component({
  selector: 'app-site-objective-energy-carbon',
  templateUrl: './site-objective-energy-carbon.component.html',
  styleUrls: ['./site-objective-energy-carbon.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    NbAccordionModule,
  ]
})
export class SiteObjectiveEnergyCarbonComponent implements OnInit, OnDestroy {
  @Input() perimeter: KPI.Perimeter;
  @Input() readOnly: boolean = false;

  public energyObjectives: EnergyObjective[] = [];
  public carbonObjectives: CarbonObjective[] = [];
  public loading = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private energyObjectivesService: EnergyObjectivesService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private saveToast: SaveToastService
  ) { }

  ngOnInit(): void {
    this.loadBothObjectives();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  loadBothObjectives(): void {
    this.loadEnergyObjectives();
    this.loadCarbonObjectives();
  }

  loadEnergyObjectives(): void {
    if (!this.perimeter) return;

    this.loading = true;
    const startYear = 2022;
    const endYear = 2050;

    this.subscriptions.push(
      this.energyObjectivesService.getEnergyObjectives(this.perimeter, startYear, endYear)
        .subscribe({
          next: (objectives) => {
            this.energyObjectives = [...objectives];
            this.loading = false;
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error loading energy objectives:', error);
            this.toastrService.danger(this.translate.instant('Error loading energy objectives'), this.translate.instant('Error'));
            this.loading = false;
          }
        })
    );
  }

  loadCarbonObjectives(): void {
    if (!this.perimeter) return;

    const startYear = 2022;
    const endYear = 2050;

    this.subscriptions.push(
      this.energyObjectivesService.getCarbonObjectives(this.perimeter, startYear, endYear)
        .subscribe({
          next: (objectives) => {
            this.carbonObjectives = [...objectives];
            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error loading carbon objectives:', error);
            this.toastrService.danger(this.translate.instant('Error loading carbon objectives'), this.translate.instant('Error'));
          }
        })
    );
  }

  private setSaved(): void {
    this.saveToast.showSaved();
  }

  addEnergyObjective(): void {
    const dialogRef = this.dialogService.open(AddEnergyObjectiveDialogComponent, {
      context: {
        perimeter: this.perimeter,
        isEditing: false
      },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.loadBothObjectives();
        this.setSaved();
      }
    });
  }

  addCarbonObjective(): void {
    const dialogRef = this.dialogService.open(AddCarbonObjectiveDialogComponent, {
      context: {
        perimeter: this.perimeter,
        isEditing: false
      },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.loadBothObjectives();
        this.setSaved();
      }
    });
  }

  editEnergyObjective(objective: EnergyObjective): void {
    const dialogRef = this.dialogService.open(EditEnergyObjectiveDialogComponent, {
      context: {
        perimeter: this.perimeter,
        objective: this.adaptForDialog(objective)
      },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.loadBothObjectives();
        this.setSaved();
      }
    });
  }

  editCarbonObjective(objective: CarbonObjective): void {
    const dialogRef = this.dialogService.open(EditCarbonObjectiveDialogComponent, {
      context: {
        perimeter: this.perimeter,
        objective: this.adaptCarbonObjectiveForDialog(objective)
      },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.loadBothObjectives();
        this.setSaved();
      }
    });
  }

  private adaptForDialog(objective: EnergyObjective): any {
    return {
      ...objective,
      building: objective.building_name || objective.building || '',
      objectiveValue: objective.consumption || objective.objectiveValue || 0
    };
  }

  private adaptCarbonObjectiveForDialog(objective: CarbonObjective): any {
    return {
      ...objective,
      building: objective.building_name || objective.building || '',
      objectiveValue: objective.emission || objective.objectiveValue || 0
    };
  }

  confirmDeleteEnergyObjective(objective: EnergyObjective): void {
    const dialogRef = this.dialogService.open(ConfirmationDialogComponent, {
      context: {
        label: this.translate.instant('Are you sure you want to delete the energy objective for {{building}} ({{year}})?', { building: objective.building, year: objective.year })
      },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.deleteEnergyObjective(objective.id);
      }
    });
  }

  deleteEnergyObjective(id: number | string): void {
    this.saveToast.showSaving();
    this.subscriptions.push(
      this.energyObjectivesService.deleteEnergyObjective(Number(id)).subscribe({
        next: () => {
          this.loadBothObjectives();
          this.setSaved();
        },
        error: (error) => {
          console.error('Error deleting energy objective:', error);
          this.saveToast.close();
          this.toastrService.danger(this.translate.instant('Error deleting energy objective'), this.translate.instant('Error'));
        }
      })
    );
  }

  confirmDeleteCarbonObjective(objective: CarbonObjective): void {
    const dialogRef = this.dialogService.open(ConfirmationDialogComponent, {
      context: {
        label: this.translate.instant('Are you sure you want to delete the carbon objective for {{building}} ({{year}})?', { building: objective.building, year: objective.year })
      },
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });

    dialogRef.onClose.subscribe((result) => {
      if (result) {
        this.deleteCarbonObjective(objective.id);
      }
    });
  }

  deleteCarbonObjective(id: number | string): void {
    this.saveToast.showSaving();
    this.subscriptions.push(
      this.energyObjectivesService.deleteCarbonObjective(Number(id))
        .subscribe({
          next: () => {
            this.loadBothObjectives();
            this.setSaved();
          },
          error: (error) => {
            console.error('Error deleting carbon objective:', error);
            this.saveToast.close();
            this.toastrService.danger(this.translate.instant('Error deleting carbon objective'), this.translate.instant('Error'));
          }
        })
    );
  }
}
